import React, { FormEvent, useEffect, useState } from 'react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Toast from '../components/common/Toast';
import { apiService } from '../services/api';
import { useEscapeToClose } from '../hooks/useEscapeToClose';
import '../components/Auth/RegisterModal.css';
import './ManageCompetitionModals.css';

interface ManageCompetitionAddHostModalProps {
  isOpen: boolean;
  onClose: () => void;
  competitionId: number;
}

const ManageCompetitionAddHostModal: React.FC<ManageCompetitionAddHostModalProps> = ({
  isOpen,
  onClose,
  competitionId,
}) => {
  const [identifier, setIdentifier] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setIdentifier('');
    setIsLoading(false);
    setError('');
    setToastMessage('');
  }, [isOpen]);

  useEscapeToClose(onClose, { enabled: isOpen && !isLoading, confirmIfDirty: () => identifier.trim() !== '' });

  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    const ident = identifier.trim();
    if (!ident) {
      setError('Enter a netid (username) or email');
      return;
    }

    setIsLoading(true);
    try {
      // Manage page uses add_basic so host-admins can add basic hosts.
      // (Platform admins can still add host-admins from the Admin page.)
      const res = await apiService.post('/hosts/add_basic', {
        username: ident,
        competition_id: competitionId,
      });
      setToastMessage(`Host ${res.data?.action || 'updated'}: ${res.data?.username || ident}`);
    } catch (err: any) {
      console.error('Failed to add host', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to add host';
      setError(String(msg));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="register-modal-backdrop" onClick={handleBackdropClick}>
        <div
          className="register-modal manage-addhost-modal"
          role="dialog"
          aria-modal="true"
          style={{ width: 'min(720px, 96vw)', maxWidth: '720px' }}
        >
          <div className="register-modal__header">
            <h2 className="register-modal__title">Add Host</h2>
            <button
              type="button"
              className="register-modal__close"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isLoading}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <form className="register-modal__form" onSubmit={handleSubmit}>
            {error && (
              <div className="register-modal__error" role="alert">
                {error}
              </div>
            )}

            <div className="register-modal__fields">
              <Input
                name="identifier"
                label="User (netid or email)"
                placeholder="netid or user@domain.com"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={isLoading}
              />

              <p className="mcm-text-secondary" style={{ marginTop: '0.25rem', fontSize: '0.9rem' }}>
                Hosts added from this page are granted <strong>basic</strong> host privilege.
              </p>
            </div>

            <div className="register-modal__actions">
              <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>
                Close
              </Button>
              <Button type="submit" variant="primary" loading={isLoading} disabled={isLoading}>
                Add Host
              </Button>
            </div>
          </form>
        </div>
      </div>

      {toastMessage && <Toast message={toastMessage} duration={2500} onClose={() => setToastMessage('')} />}
    </>
  );
};

export default ManageCompetitionAddHostModal;
